import * as React from 'react';
import groundImage from '../../../../../images/ground_pattern.svg';
import { ISlopedWeirLongitudinalProps } from './ISlopedWeir';
import { getImgSrcWithColor, getTextureByMaterial, loadImage } from '../../../../../utils/graphicEngine';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { setDesignImg } from '../../../../store/project/resultsSlice';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { roundToOne, roundToTwo } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//SETUP
const setup = {
    gabionColor: 'grey',
    borderGabionColor: 'black',
    errorColor: 'red',
    waterColor: '#83d7ee',

    borderWidth: 1,

    indicatorHeight: 30,
    indicatorWidth: 55,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    counterweirWidth: 1,
    counterweirUnderSoil: 0.5,

    heightStillingBasin: 0.5,

    heightSoil: 1,
};
//

const SlopedWeirLongitudinal = (props: ISlopedWeirLongitudinalProps) => {
    const slopedWeirCanvas = React.useRef<HTMLCanvasElement>(null);

    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let stillingBasinImageSrc: HTMLImageElement | undefined = undefined;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    const { scale, result, inspectAttribute } = props;

    const dispatch = useAppDispatch();

    const stillingBasin = { ...props.stillingBasin };
    const wingsGeometry = { ...props.wingsGeometry };
    const weirGeometry = { ...props.weirGeometry };

    let heightFromSoil = result ? Math.max(result.hydraulic.upstreamSection, result.hydraulic.criticalHeight, wingsGeometry.height!) : wingsGeometry.height!;

    const getResultWidth = (): number => {
        let widthResult = 0;
        if (!!result) {
            if (result.basinDesign.jumpLength + 0.5 > stillingBasin.poolLength! + setup.counterweirWidth) {
                widthResult = result.basinDesign.jumpLength + 0.5 - stillingBasin.poolLength! - setup.counterweirWidth;
            }
        }
        return widthResult;
    };

    const _height =
        heightFromSoil +
        weirGeometry.liningThickness! +
        weirGeometry.height! +
        (weirGeometry.liningThickness! < setup.counterweirUnderSoil ? setup.counterweirUnderSoil - weirGeometry.liningThickness! : 0) +
        setup.heightSoil +
        setup.indicatorHeight/scale;

    const _width =
        setup.indicatorWidth/scale +
        setup.borderWidth +
        weirGeometry.crestLength! +
        weirGeometry.height! / weirGeometry.stepRatio! +
        stillingBasin.poolLength! +
        setup.counterweirWidth +
        +getResultWidth() +
        setup.borderWidth +
        setup.indicatorWidth/scale;

    inspectAttribute(_width, _height);

    React.useEffect(() => {
        let slopedWeirCanvasEle = slopedWeirCanvas.current!;
        ctx = slopedWeirCanvasEle.getContext('2d')!;
        Promise.all([loadStillingBasinImage(), loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!result) {
                createWaterSection();
            }
            createSoil();
            createWeirGeometry();
            createStillingBasin();
            createIndicator();
            dispatch(setDesignImg(slopedWeirCanvasEle.toDataURL('image/png')));
        });
    }, [props]);

    const cleanCanvas = () => {
        let canvasEle = slopedWeirCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const createIndicator = () => {
        //wings height
        createIndicatorY(heightFromSoil - wingsGeometry.height!, heightFromSoil, setup.indicatorWidth/scale / 2, wingsGeometry.height!);
        //weirGeometry height
        createIndicatorY(heightFromSoil, heightFromSoil + weirGeometry.height!, setup.indicatorWidth/scale / 2, weirGeometry.height!);
        //weirGeometry lining thickness
        createIndicatorY(heightFromSoil + weirGeometry.height!, heightFromSoil + weirGeometry.height! + weirGeometry.liningThickness!, setup.indicatorWidth/scale / 2, weirGeometry.liningThickness!);
        //weirGeometry crest length
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth,
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!,
            _height - setup.indicatorHeight/scale / 2,
            weirGeometry.crestLength!,
        );
        //weirGeometry inclination length
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!,
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!,
            _height - setup.indicatorHeight/scale / 2,
            roundToTwo(weirGeometry.height! / weirGeometry.stepRatio!),
        );
        //stilling basin length
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!,
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!,
            _height - setup.indicatorHeight/scale / 2,
            stillingBasin.poolLength!,
        );
        //stilling basin Counterweir
        createIndicatorX(
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!,
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength! + setup.counterweirWidth,
            _height - setup.indicatorHeight/scale / 2,
            setup.counterweirWidth,
        );
        createIndicatorY(
            heightFromSoil + weirGeometry.height! - stillingBasin.counterweirElevation!,
            heightFromSoil + weirGeometry.height!,
            _width - setup.indicatorWidth/scale / 2,
            stillingBasin.counterweirElevation!,
        );
        createIndicatorY(heightFromSoil + weirGeometry.height!, heightFromSoil + weirGeometry.height! + setup.counterweirUnderSoil, _width - setup.indicatorWidth/scale / 2, setup.counterweirUnderSoil);
    };

    const createWaterSection = () => {
        let z0 = result!.hydraulic.upstreamSection;
        let z1 = result!.basinDesign.downstreamWaterDepth;
        let z2 = result!.basinDesign.conjugateDepth;
        let z3 = result!.hydraulic.z3;
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(scale * setup.indicatorWidth/scale, scale * (heightFromSoil - z0));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! - 1), scale * (heightFromSoil - z0));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * (heightFromSoil - result!.hydraulic.criticalHeight));
        //TODO
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil + weirGeometry.height! - z1),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + (result!.basinDesign.jumpLength * 2) / 3),
            scale * (heightFromSoil + weirGeometry.height! - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + result!.basinDesign.jumpLength),
            scale * (heightFromSoil + weirGeometry.height! - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + result!.basinDesign.jumpLength + 0.5),
            scale * (heightFromSoil + weirGeometry.height! - z3),
        );
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (heightFromSoil + weirGeometry.height! - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * (heightFromSoil - z0));
        ctx.fill();
        ctx.closePath();
    };

    const loadStillingBasinImage = async (): Promise<void> => {
        let texture = getTextureByMaterial(stillingBasin.material.type, stillingBasin.material.category);
        if (!!texture) {
            let imgSrcWithColor = getImgSrcWithColor(texture, stillingBasin.material.color);
            stillingBasinImageSrc = await loadImage(imgSrcWithColor);
        }
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const createSoil = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.fillStyle = pat;

        ctx.beginPath();
        ctx.moveTo(scale * setup.indicatorWidth/scale, scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!), scale * (heightFromSoil + weirGeometry.height!));
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (heightFromSoil + weirGeometry.height!));
        ctx.lineTo(scale * (_width - setup.indicatorWidth/scale), scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * (_height - setup.indicatorHeight/scale));
        ctx.lineTo(scale * setup.indicatorWidth/scale, scale * heightFromSoil);
        ctx.fill();
        ctx.closePath();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], scale * x - 2, scale * moveToY, scale * x + 2, scale * moveToY);
        createLine([], scale * x, scale * moveToY, scale * x, scale * lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * x - 2, scale * lineToY, scale * x + 2, scale * lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], scale * moveToX, scale * y - 2, scale * moveToX, scale * y + 2);
        createLine([], scale * moveToX, scale * y, scale * lineToX, scale * y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * lineToX, scale * y - 2, scale * lineToX, scale * y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const createWeirGeometry = () => {
        let pat = !!stillingBasinImageSrc ? ctx.createPattern(stillingBasinImageSrc, 'repeat') : stillingBasin.material.color;
        //gabions
        ctx.beginPath();
        ctx.fillStyle = pat!;
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.moveTo(scale * (setup.indicatorWidth/scale + setup.borderWidth), scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!), scale * (heightFromSoil + weirGeometry.height!));
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil + weirGeometry.height! + weirGeometry.liningThickness!),
        );
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * (heightFromSoil + weirGeometry.liningThickness!));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth), scale * (heightFromSoil + weirGeometry.liningThickness!));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth), scale * heightFromSoil);
        ctx.fill();
        ctx.stroke();
        ctx.closePath();

        //wings
        ctx.save();

        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(scale * (setup.indicatorWidth/scale + setup.borderWidth), scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth), scale * (heightFromSoil - wingsGeometry.height!));
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * (heightFromSoil - wingsGeometry.height!));
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.stroke();
        ctx.closePath();

        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * heightFromSoil);
        ctx.lineTo(scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength!), scale * (heightFromSoil - wingsGeometry.height!));
        ctx.stroke();
        ctx.closePath();

        ctx.beginPath();
        ctx.setLineDash([5, 5]);
        ctx.moveTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil - wingsGeometry.height! + weirGeometry.height!),
        );
        ctx.stroke();
        ctx.closePath();

        ctx.restore();
    };

    const createStillingBasin = () => {
        let patStilling = !!stillingBasinImageSrc ? ctx.createPattern(stillingBasinImageSrc, 'repeat') : stillingBasin.material.color;
        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');
        let isThereStillingBasinWidthError_Macra2_Sloped = stillingBasin?.poolLength! < result?.basinDesign?.jumpLength!;
        let isThereCounterweirElevationError_Macra2_Sloped = stillingBasin?.counterweirElevation! < result?.basinDesign?.minCounterweirHeight!;

        createRectWithBorder(
            scale * (setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio!),
            scale * (heightFromSoil + weirGeometry.height!),
            scale * stillingBasin.poolLength!,
            scale * stillingBasin.material.physicalData![0].thickness!,
            isThereStillingBasinWidthError_Macra2_Sloped ? setup.errorColor : patStilling!,
        );

        handleGabionOverHeight(
            setup.indicatorWidth/scale + setup.borderWidth + weirGeometry.crestLength! + weirGeometry.height! / weirGeometry.stepRatio! + stillingBasin.poolLength!,
            heightFromSoil + weirGeometry.height! - stillingBasin.counterweirElevation!,
            setup.counterweirWidth,
            stillingBasin.counterweirElevation! + setup.counterweirUnderSoil,
            isThereCounterweirElevationError_Macra2_Sloped ? setup.errorColor : basePat!,
        );
    };

    const handleGabionOverHeight = (x: number, y: number, w: number, h: number, colorGabion?: string | CanvasPattern | CanvasGradient) => {
        if (h > 1) {
            for (let i = 1; i <= h; i++) {
                colorGabion ? createRectWithBorder(scale * x, scale * (y + h - i), scale * w, scale * 1, colorGabion) : ctx.strokeRect(scale * x, scale * (y + h - i), scale * w, scale * 1);
            }
            if (h - Math.trunc(h) > 0) {
                colorGabion
                    ? createRectWithBorder(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)), colorGabion)
                    : ctx.strokeRect(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)));
            }
        } else {
            colorGabion ? createRectWithBorder(scale * x, scale * y, scale * w, scale * h, colorGabion) : ctx.strokeRect(scale * x, scale * y, scale * w, scale * h);
        }
    };

    const createRectWithBorder = (x: number, y: number, w: number, h: number, pat: string | CanvasPattern | CanvasGradient) => {
        ctx.fillStyle = pat;
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.strokeRect(x, y, w, h);
    };
   
    return (
        <div className='position-absolute'>
            <canvas width={scale * _width} height={scale * _height} ref={slopedWeirCanvas}></canvas>
        </div>
    );
};

export default SlopedWeirLongitudinal;
